import React, { useContext, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import toast from "react-hot-toast";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { AuthContext } from "../context/AuthContext";
import { motion } from "framer-motion";

const { REACT_APP_BACKEND_URL } = process.env;

const Login = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    otp: "",
    showPassword: false,
    step: parseInt(sessionStorage.getItem('loginStep')) || 1,
  });
  const { email, password, otp, showPassword, step } = formData;
  const navigate = useNavigate();
  const { login } = useContext(AuthContext);
  const [userId, setUserId] = useState(sessionStorage.getItem('loginUserId') || null);
  const [isLoading, setIsLoading] = useState(false);

  const onChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const togglePasswordVisibility = () => {
    setFormData({ ...formData, showPassword: !showPassword });
  };

  const handleResendOTP = async () => {
    if (isLoading) return;

    setIsLoading(true);
    try {
      const res = await axios.post(`${REACT_APP_BACKEND_URL}/api/auth/login`, { email, password });
      toast.success("OTP resent successfully! ✅");
    } catch (err) {
      console.error(err.response?.data || err);
      toast.error(err.response?.data?.msg || "Failed to resend OTP 🚫");
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToLogin = () => {
    sessionStorage.removeItem('loginUserId');
    sessionStorage.removeItem('loginStep');
    setUserId(null);
    setFormData({ email: "", password: "", otp: "", showPassword: false, step: 1 });
  };

  const onSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isLoading) return;

    if (step === 1) {
      setIsLoading(true);
      try {
        const res = await axios.post(`${REACT_APP_BACKEND_URL}/api/auth/login`, { email, password });
        const newUserId = res.data.userId;
        setUserId(newUserId);

        // Persist to sessionStorage
        sessionStorage.setItem('loginUserId', newUserId);
        sessionStorage.setItem('loginStep', '2');

        setFormData({ ...formData, step: 2 });
        toast.success(res.data.msg);
      } catch (err) {
        console.error(err.response?.data || err);
        toast.error(err.response?.data?.msg || "Login failed 🚫🚫");
      } finally {
        setIsLoading(false);
      }
    } else if (step === 2) {
      // Check if userId exists
      if (!userId) {
        toast.error("Session expired. Please login again.");
        sessionStorage.removeItem('loginUserId');
        sessionStorage.removeItem('loginStep');
        setFormData({ ...formData, step: 1 });
        return;
      }

      setIsLoading(true);
      try {
        const res = await axios.post(`${REACT_APP_BACKEND_URL}/api/auth/verify-otp`, { userId, otp });

        // Clear session storage after successful login
        sessionStorage.removeItem('loginUserId');
        sessionStorage.removeItem('loginStep');

        login(res.data.token, res.data.user);
        toast.success(res.data.msg);
        navigate("/dashboard");
      } catch (err) {
        console.error(err.response?.data || err);
        toast.error(err.response?.data?.msg || "OTP verification failed 🚫🚫");
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="bg-white bg-opacity-10 backdrop-filter backdrop-blur-lg shadow-2xl rounded-2xl p-8 w-full max-w-md"
      >
        <h2 className="text-3xl font-bold mb-8 text-center text-white">
          {step === 1 ? "Welcome Back" : "Verify OTP"}
        </h2>
        <form onSubmit={onSubmit} className="space-y-6">
          {step === 1 && (
            <>
              <div className="relative">
                <input
                  type="email"
                  placeholder="Email"
                  name="email"
                  value={email}
                  onChange={onChange}
                  required
                  className="w-full px-4 py-3 bg-gray-700 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 pointer-events-none">
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                    <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                  </svg>
                </div>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  name="password"
                  value={password}
                  minLength="8"
                  onChange={onChange}
                  className="w-full px-4 py-3 bg-gray-700 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? (
                    <AiOutlineEyeInvisible className="text-gray-400 h-5 w-5" />
                  ) : (
                    <AiOutlineEye className="text-gray-400 h-5 w-5" />
                  )}
                </button>
              </div>
            </>
          )}
          {step === 2 && (
            <div className="relative">
              <input
                type="text"
                placeholder="Enter OTP"
                name="otp"
                value={otp}
                onChange={onChange}
                required
                className="w-full px-4 py-3 bg-gray-700 bg-opacity-50 text-white placeholder-gray-400 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 transition duration-300"
              />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 pointer-events-none">
                <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1.323l3.954 1.582 1.599-.8a1 1 0 01.894 1.79l-1.233.616 1.738 5.42a1 1 0 01-.285 1.05A3.989 3.989 0 0115 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.715-5.349L11 6.477V16h2a1 1 0 110 2H7a1 1 0 110-2h2V6.477L6.237 7.582l1.715 5.349a1 1 0 01-.285 1.05A3.989 3.989 0 015 15a3.989 3.989 0 01-2.667-1.019 1 1 0 01-.285-1.05l1.738-5.42-1.233-.616a1 1 0 01.894-1.79l1.599.8L9 4.323V3a1 1 0 011-1zm-5 8.274l-.818 2.552c.25.112.526.174.818.174.292 0 .569-.062.818-.174L5 10.274zm10 0l-.818 2.552c.25.112.526.174.818.174.292 0 .569-.062.818-.174L15 10.274z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
          )}
          <motion.button
            whileHover={{ scale: isLoading ? 1 : 1.05, boxShadow: "0 0 15px rgba(59, 130, 246, 0.5)" }}
            whileTap={{ scale: isLoading ? 1 : 0.95 }}
            type="submit"
            disabled={isLoading}
            className={`w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold text-lg shadow-lg hover:from-blue-600 hover:to-indigo-700 transition duration-300 ${
              isLoading ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isLoading ? "Processing..." : step === 1 ? "Login" : "Verify OTP"}
          </motion.button>
          {step === 1 && (
             <div className="text-center mt-6 space-y-2">
             {/* <p className="text-gray-400">
               Don't have an account?{" "}
               <Link
                 to="/register"
                 className="text-gray-300 font-semibold hover:underline hover:text-gray-200 transition duration-300"
               >
                 Register
               </Link>
             </p> */}
             <p className="text-gray-300">
               <Link
                 to="/forgot-password"
                 className="text-gray-300 font-semibold hover:underline hover:text-gray-200 transition duration-300"
               >
                 Forgot Password?
               </Link>
             </p>
           </div>
          )}
          {step === 2 && (
            <div className="text-center mt-6 space-y-3">
              <button
                type="button"
                onClick={handleResendOTP}
                disabled={isLoading}
                className="text-blue-400 font-semibold hover:underline hover:text-blue-300 transition duration-300"
              >
                Resend OTP
              </button>
              <p>
                <button
                  type="button"
                  onClick={handleBackToLogin}
                  className="text-gray-400 hover:text-gray-300 transition duration-300"
                >
                  ← Back to Login
                </button>
              </p>
            </div>
          )}
        </form>
      </motion.div>
    </div>
  );
};

export default Login;